/**
 * useCvState
 *
 * Owns the core CV editor state:
 * - activeTemplate    : currently selected template id
 * - templateMeta      : list of { id, name, lang?, linkedVersionId? } versions
 * - allTemplatesData  : map from template id → CVData (per-version snapshots)
 * - commonInfo        : shared personal info injected into every version
 * - fieldVisibility   : per-field toggles for the header
 * - data              : the "active" CVData = allTemplatesData[activeTemplate]
 *                        merged with commonInfo at display time
 *
 * Also owns the persistence effects (localStorage) and the three sync
 * effects that keep everything coherent:
 *   (a) add missing per-template snapshots when templateMeta grows
 *   (b) push edits on `data` back into allTemplatesData[activeTemplate]
 *   (c) rebuild `data` when switching template / editing commonInfo
 *
 * The hook exposes setters for external handlers (add/duplicate/delete/
 * translate/autoIa/import) and `isSyncingDataRef` for the edge case where
 * upstream code needs to inhibit the (b) → (c) cycle.
 *
 * Behavior is intentionally identical to the previous inline App.tsx code.
 */

import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import type { CVData, TemplateMap } from '../types';
import { defaultCV } from '../types';
import templatesData from '../templates.json';
import {
  safeLocalStorageGet,
  safeLocalStorageGetJson,
  safeLocalStorageSet,
  safeLocalStorageSetJson,
  warnOnQuotaOnce,
} from '../lib/safe-storage';

/** Clé localStorage qui trackle la dernière modification locale du state CV.
 *  Utilisée par useCvSync pour la résolution de conflits serveur/local. */
export const CV_STATE_UPDATED_AT_KEY = 'cv_state_updated_at';

/** Marque le localStorage comme modifié maintenant (pour résolution de conflits sync). */
export function touchLocalUpdatedAt(): void {
  try {
    localStorage.setItem(CV_STATE_UPDATED_AT_KEY, new Date().toISOString());
  } catch { /* ignore */ }
}

const templates = templatesData as TemplateMap;
const defaultTemplateMeta = Object.values(templates).map((tpl) => ({
  id: tpl.id,
  name: tpl.name,
}));

export type TemplateMetaItem = {
  id: string;
  name: string;
  lang?: 'fr' | 'en';
  linkedVersionId?: string;
};

export type CommonInfo = {
  firstName: string;
  lastName: string;
  civility: '' | 'M.' | 'Mme.';
  fullName: string;
  email: string;
  phone: string;
  location: string;
  city: string;
  country: string;
  birthDate: string;
  avatar: string;
  compressedAvatar: string;
  nationality: string;
  birthPlace: string;
  gender: string;
  maritalStatus: string;
  [extra: string]: unknown;
};

export type FieldVisibility = {
  avatar: boolean;
  civility: boolean;
  firstName: boolean;
  lastName: boolean;
  fullName: boolean;
  email: boolean;
  phone: boolean;
  birthDate: boolean;
  city: boolean;
  country: boolean;
  nationality: boolean;
  birthPlace: boolean;
  gender: boolean;
  maritalStatus: boolean;
};

const DEFAULT_FIELD_VISIBILITY: FieldVisibility = {
  avatar: true,
  civility: false,
  firstName: true,
  lastName: true,
  fullName: true,
  email: true,
  phone: true,
  birthDate: true,
  city: true,
  country: true,
  nationality: false,
  birthPlace: false,
  gender: false,
  maritalStatus: false,
};

function initActiveTemplate(): string {
  return safeLocalStorageGet('cv_active_template', 'classique') || 'classique';
}

function initTemplateMeta(): TemplateMetaItem[] {
  const parsed = safeLocalStorageGetJson<unknown>('cv_templates_meta');
  if (parsed) {
    try {
      if (Array.isArray(parsed) && parsed.length > 0) {
        const filtered = (parsed as Array<Record<string, unknown>>)
          .filter((item) => item?.id && item?.name)
          .map((item) => ({
            id: item.id as string,
            name: item.name as string,
            ...(item.lang && { lang: item.lang as 'fr' | 'en' }),
            ...(item.linkedVersionId && { linkedVersionId: item.linkedVersionId as string }),
          }));
        if (filtered.length > 0) return filtered;
      }
    } catch (e) {
      console.error('Failed to parse templates meta', e);
    }
  }
  return defaultTemplateMeta as TemplateMetaItem[];
}

function initAllTemplatesData(): Record<string, CVData> {
  const parsed = safeLocalStorageGetJson<Record<string, CVData>>('cv_all_templates_data');
  if (parsed && typeof parsed === 'object') return parsed;
  return Object.keys(templates).reduce(
    (acc, key) => {
      const tpl = templates[key];
      if (tpl) acc[key] = tpl.data;
      return acc;
    },
    {} as Record<string, CVData>,
  );
}

function initCommonInfo(): CommonInfo {
  const rawParsed = safeLocalStorageGetJson<Record<string, unknown>>('cv_common_info');
  if (rawParsed) {
    try {
      const parsed = rawParsed as Record<string, unknown>;
      const location = typeof parsed.location === 'string' ? parsed.location : '';
      if (location && (!parsed.city || !parsed.country)) {
        const parts = location.split(',');
        if (parts.length >= 2) {
          parsed.city = parsed.city || parts[0]?.trim() || '';
          parsed.country = parsed.country || parts.slice(1).join(',').trim() || '';
        } else if (parts.length === 1) {
          parsed.city = parsed.city || parts[0]?.trim() || '';
          parsed.country = parsed.country || '';
        }
      }
      if (parsed.fullName && !parsed.firstName && !parsed.lastName) {
        const parts = String(parsed.fullName).trim().split(/\s+/);
        parsed.firstName = parts[0] || '';
        parsed.lastName = parts.slice(1).join(' ') || '';
      }
      const gender = typeof parsed.gender === 'string' ? parsed.gender : '';
      if (gender === 'M.' || gender === 'Mme' || gender === 'Mme.') {
        parsed.civility = gender === 'Mme' || gender === 'Mme.' ? 'Mme.' : 'M.';
        parsed.gender = '';
      }
      return parsed as unknown as CommonInfo;
    } catch (e) {
      console.error('Failed to parse saved info', e);
    }
  }
  const initialTemplate = (templates['classique'] ||
    Object.values(templates)[0]) as (typeof templates)[string];
  const pi = initialTemplate.data.personalInfo as Record<string, unknown>;
  const fullName = (pi.fullName as string) || 'Élie Mimouni';
  const nameParts = fullName.trim().split(/\s+/);
  return {
    firstName: (pi.firstName as string) || nameParts[0] || '',
    lastName: (pi.lastName as string) || nameParts.slice(1).join(' ') || '',
    civility: (pi.civility as '' | 'M.' | 'Mme.') || '',
    fullName,
    email: initialTemplate.data.personalInfo.email,
    phone: initialTemplate.data.personalInfo.phone,
    location: initialTemplate.data.personalInfo.location,
    city:
      initialTemplate.data.personalInfo.city ||
      initialTemplate.data.personalInfo.location?.split(',')[0]?.trim() ||
      '',
    country:
      initialTemplate.data.personalInfo.country ||
      initialTemplate.data.personalInfo.location?.split(',')[1]?.trim() ||
      '',
    birthDate: initialTemplate.data.personalInfo.birthDate || '',
    avatar: initialTemplate.data.personalInfo.avatar || '',
    compressedAvatar: '',
    nationality: initialTemplate.data.personalInfo.nationality || '',
    birthPlace: initialTemplate.data.personalInfo.birthPlace || '',
    gender: initialTemplate.data.personalInfo.gender || '',
    maritalStatus: initialTemplate.data.personalInfo.maritalStatus || '',
  };
}

function initFieldVisibility(): FieldVisibility {
  const parsed = safeLocalStorageGetJson<Record<string, unknown>>('cv_field_visibility');
  if (parsed) {
    try {
      const hideName =
        parsed.fullName === false &&
        parsed.firstName === undefined &&
        parsed.lastName === undefined;
      return {
        avatar: parsed.avatar !== false,
        civility: parsed.civility === true,
        firstName: parsed.firstName !== false && !hideName,
        lastName: parsed.lastName !== false && !hideName,
        fullName: parsed.fullName !== false,
        email: parsed.email !== false,
        phone: parsed.phone !== false,
        birthDate: parsed.birthDate !== false,
        city: parsed.city !== false,
        country: parsed.country !== false,
        nationality: parsed.nationality === true,
        birthPlace: parsed.birthPlace === true,
        gender: parsed.gender === true,
        maritalStatus: parsed.maritalStatus === true,
      };
    } catch (e) {
      console.error('Failed to parse field visibility', e);
    }
  }
  return { ...DEFAULT_FIELD_VISIBILITY };
}

export interface UseCvStateReturn {
  activeTemplate: string;
  setActiveTemplate: Dispatch<SetStateAction<string>>;

  templateMeta: TemplateMetaItem[];
  setTemplateMeta: Dispatch<SetStateAction<TemplateMetaItem[]>>;

  allTemplatesData: Record<string, CVData>;
  setAllTemplatesData: Dispatch<SetStateAction<Record<string, CVData>>>;

  commonInfo: CommonInfo;
  setCommonInfo: Dispatch<SetStateAction<CommonInfo>>;

  fieldVisibility: FieldVisibility;
  setFieldVisibility: Dispatch<SetStateAction<FieldVisibility>>;

  data: CVData;
  setData: Dispatch<SetStateAction<CVData>>;

  /** Set to true around programmatic data writes to inhibit the data↔allTemplatesData
   *  sync-back effect (prevents echo loops when bulk replacing a template snapshot). */
  isSyncingDataRef: React.MutableRefObject<boolean>;
}

export function useCvState(): UseCvStateReturn {
  const [activeTemplate, setActiveTemplate] = useState<string>(initActiveTemplate);
  const [templateMeta, setTemplateMeta] = useState<TemplateMetaItem[]>(initTemplateMeta);
  const [allTemplatesData, setAllTemplatesData] =
    useState<Record<string, CVData>>(initAllTemplatesData);
  const [commonInfo, setCommonInfo] = useState<CommonInfo>(initCommonInfo);
  const [fieldVisibility, setFieldVisibility] = useState<FieldVisibility>(initFieldVisibility);

  const isSyncingDataRef = useRef(false);

  const [data, setData] = useState<CVData>(() => {
    const currentTpl = (allTemplatesData[activeTemplate] ||
      Object.values(allTemplatesData)[0]) as CVData;
    return {
      ...currentTpl,
      personalInfo: {
        ...currentTpl.personalInfo,
        ...commonInfo,
      },
    };
  });

  // ---- persistence ----
  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSet('cv_active_template', activeTemplate));
    touchLocalUpdatedAt();
  }, [activeTemplate]);

  useEffect(() => {
    if (templateMeta.length > 0) {
      void warnOnQuotaOnce(safeLocalStorageSetJson('cv_templates_meta', templateMeta));
      touchLocalUpdatedAt();
    }
  }, [templateMeta]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSetJson('cv_common_info', commonInfo));
    touchLocalUpdatedAt();
  }, [commonInfo]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSetJson('cv_field_visibility', fieldVisibility));
    touchLocalUpdatedAt();
  }, [fieldVisibility]);

  useEffect(() => {
    void warnOnQuotaOnce(safeLocalStorageSetJson('cv_all_templates_data', allTemplatesData));
    touchLocalUpdatedAt();
  }, [allTemplatesData]);

  // ---- consistency effects ----

  // Ensure activeTemplate points to a real version.
  useEffect(() => {
    if (templateMeta.length === 0) return;
    if (!templateMeta.some((tpl) => tpl.id === activeTemplate)) {
      const nextActive = templateMeta[0]?.id;
      if (nextActive) setActiveTemplate(nextActive);
    }
  }, [templateMeta, activeTemplate]);

  // Ensure each template has a backing CVData snapshot.
  useEffect(() => {
    if (templateMeta.length === 0) return;
    setAllTemplatesData((prev) => {
      let changed = false;
      const next = { ...prev };
      templateMeta.forEach((tpl) => {
        if (!next[tpl.id]) {
          const fallbackData = prev[activeTemplate] || Object.values(prev)[0] || defaultCV;
          next[tpl.id] = cloneCvData(fallbackData);
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [templateMeta, activeTemplate]);

  // Push local `data` edits into the per-template snapshot map.
  //
  // IMPORTANT: this effect must only fire on `data` changes, NOT on
  // `activeTemplate` changes. If activeTemplate were in the deps, switching
  // versions would fire this effect BEFORE the "rebuild data on template
  // switch" effect below, and would overwrite the target template's saved
  // data with the CURRENTLY DISPLAYED data — corrupting it and making the
  // version switcher appear to do nothing.
  //
  // We read the current activeTemplate via a ref so the eslint exhaustive-deps
  // rule stays happy without re-introducing the bug.
  const activeTemplateRef = useRef(activeTemplate);
  useEffect(() => {
    activeTemplateRef.current = activeTemplate;
  }, [activeTemplate]);

  useEffect(() => {
    isSyncingDataRef.current = true;
    setAllTemplatesData((prev) => ({
      ...prev,
      [activeTemplateRef.current]: data,
    }));
    setTimeout(() => {
      isSyncingDataRef.current = false;
    }, 0);
  }, [data]);

  // When switching templates or editing commonInfo/fieldVisibility, rebuild `data`
  // from the target snapshot + common info + derived location.
  useEffect(() => {
    if (isSyncingDataRef.current) return;
    const targetData = allTemplatesData[activeTemplate];
    if (!targetData) return;
    if (JSON.stringify(targetData) === JSON.stringify(data)) return;

    const city = fieldVisibility.city && commonInfo.city ? commonInfo.city : '';
    const country = fieldVisibility.country && commonInfo.country ? commonInfo.country : '';
    let formattedLocation: string;
    if (city && country) formattedLocation = `${city}, ${country}`;
    else if (city) formattedLocation = city;
    else if (country) formattedLocation = country;
    else formattedLocation = commonInfo.location || '';

    setData({
      ...targetData,
      personalInfo: {
        ...targetData.personalInfo,
        ...commonInfo,
        location: formattedLocation,
      },
    });
    // `data` is intentionally excluded from deps to preserve the original
    // behavior: this effect only fires when activeTemplate / commonInfo /
    // fieldVisibility / allTemplatesData change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTemplate, commonInfo, fieldVisibility, allTemplatesData]);

  return {
    activeTemplate,
    setActiveTemplate,
    templateMeta,
    setTemplateMeta,
    allTemplatesData,
    setAllTemplatesData,
    commonInfo,
    setCommonInfo,
    fieldVisibility,
    setFieldVisibility,
    data,
    setData,
    isSyncingDataRef,
  };
}

// Re-export the default template meta for any external code that still needs it.
export { defaultTemplateMeta };

// ── Type du retour de useCvState (pour useCvSync) ─────────────────────────────
export type UseCvStateReturn = ReturnType<typeof useCvState>;

// ── Interface d'une version CV retournée par le serveur ───────────────────────
export interface ServerCvVersion {
  name: string;
  cv_data: unknown;
  updated_at: string;
}

/**
 * Fusionne les versions CV venant du serveur dans le state local (localStorage + setters React).
 *
 * Stratégie :
 * - Row '__meta__'  → commonInfo, fieldVisibility, activeTemplate, templateMeta
 * - Autres rows     → allTemplatesData[row.name] = row.cv_data
 *
 * Appeler cette fonction uniquement quand le serveur a des données plus récentes.
 */
export function mergeFromServer(
  serverVersions: ServerCvVersion[],
  setters: {
    setActiveTemplate: Dispatch<SetStateAction<string>>;
    setTemplateMeta: Dispatch<SetStateAction<TemplateMetaItem[]>>;
    setAllTemplatesData: Dispatch<SetStateAction<Record<string, CVData>>>;
    setCommonInfo: Dispatch<SetStateAction<CommonInfo>>;
    setFieldVisibility: Dispatch<SetStateAction<FieldVisibility>>;
  },
  serverUpdatedAt: string,
): void {
  const metaRow = serverVersions.find((v) => v.name === '__meta__');
  const templateRows = serverVersions.filter((v) => v.name !== '__meta__');

  // Reconstruire allTemplatesData depuis les rows serveur
  const newAllTemplatesData: Record<string, CVData> = {};
  for (const row of templateRows) {
    if (row.name && row.cv_data) {
      newAllTemplatesData[row.name] = row.cv_data as CVData;
    }
  }

  // Appliquer les métadonnées si disponibles
  if (metaRow?.cv_data) {
    const meta = metaRow.cv_data as {
      commonInfo?: CommonInfo;
      fieldVisibility?: FieldVisibility;
      activeTemplate?: string;
      templateMeta?: TemplateMetaItem[];
    };
    if (meta.templateMeta && Array.isArray(meta.templateMeta)) {
      setters.setTemplateMeta(meta.templateMeta);
    }
    if (meta.commonInfo) setters.setCommonInfo(meta.commonInfo);
    if (meta.fieldVisibility) setters.setFieldVisibility(meta.fieldVisibility);
    if (meta.activeTemplate) setters.setActiveTemplate(meta.activeTemplate);
  }

  if (Object.keys(newAllTemplatesData).length > 0) {
    setters.setAllTemplatesData(newAllTemplatesData);
  }

  // Marquer le timestamp local = timestamp serveur (évite un re-push inutile)
  try {
    localStorage.setItem(CV_STATE_UPDATED_AT_KEY, serverUpdatedAt);
  } catch { /* ignore */ }
}
